<template>
  <bottom-sheet
    :visible="visible"
    :close-icon-right-align="true"
    @close="$emit('close')"
  >
    <div
      class="book-now-inputs flex flex-col gap-3 px-3"
      :class="{
        'max-h-[100vh]': isMobile,
        'max-h-[80vh]': !isMobile,
      }"
    >
      <div class="-mt-4 text-left">
        <h4 class="text-xl text-steel-1100">Submit Offer</h4>
        <p class="text-steel-700">
          <span class="text-red-500">*</span>
          = Required Fields
        </p>
      </div>

      <div class="flex-between my-2">
        <img
          v-if="brokerLogo"
          class="flex h-8 max-w-48 items-center text-2xs"
          :src="brokerLogo"
          alt="broker logo"
        />

        <div class="flex flex-col items-end gap-2 pt-2">
          <h3 class="text-3xl font-semibold">
            {{ formatPrice(load.price) }}
          </h3>
          <p
            v-if="ratePerMile && load?.price"
            class="-mt-2 text-1xs text-neutral-subtlest"
          >
            {{ ratePerMile }}/mi
          </p>
        </div>
      </div>

      <div class="mb-4 flex flex-col gap-4">
        <p class="text-left text-lg">Booking Info</p>

        <div v-if="!use_posted_offer">
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Rate <span class="text-red-500">*</span>
          </p>
          <div class="rate-input relative">
            <p
              class="absolute left-2 top-1 z-20 mt-1 pl-2 text-left text-base font-bold text-steel-900"
            >
              $
            </p>
            <InputComponent
              input-label="rate"
              wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 pr-2 pl-[28px]"
              placeholder="Enter Rate"
              :default-input-value="form.rate"
              :value="form.rate"
              @input-change="handleInput"
            />
            <p
              class="absolute right-8 top-1.5 mt-1 pl-2 text-left font-light text-steel-900"
            >
              {{ inputRatePerMile }}/mi
            </p>
          </div>
          <p class="mt-1 pl-2 text-left text-sm font-normal text-steel-700">
            Your offer amount
          </p>
        </div>

        <div
          v-if="supportsBoth"
          class="booknow-checkbox -my-2"
        >
          <CheckboxButton
            item="Use posted offer to Book Now"
            :active="use_posted_offer"
            :handle-select="handleUsePostedOffer"
          />
        </div>

        <div v-if="request_secure_ratecon">
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Ratecon Delivery
          </p>
          <p class="pl-2 pt-3 text-left text-base">{{ rateConEmail }}</p>
        </div>

        <div
          v-else
          class="relative"
        >
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Rate Confirmation Email <span class="text-red-500">*</span>
          </p>
          <div
            class="relative flex h-10 w-full cursor-pointer items-center rounded-lg border border-steel-300"
            :class="{
              'h-16 bg-steel-100': form.ratecon_email,
              'h-10 bg-white': !form.ratecon_email,
            }"
            @click="toggleRateConDropdown"
          >
            <p class="pl-2 text-sm text-steel-700">{{ form.ratecon_email }}</p>
            <img
              class="absolute right-3 opacity-50"
              src="@/assets/arrow-down-black.svg"
              alt="carrot"
            />
          </div>
          <div
            v-if="showRateConDropdown"
            class="absolute z-20 mt-1 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
          >
            <div
              v-for="email in ratecon_emails"
              :key="email.id"
              class="flex cursor-pointer flex-col items-start border-l-4 border-transparent px-3 py-2 hover:border-sun-400 hover:bg-sun-50"
              :class="{
                '!border-sun-400 bg-sun-50': email.id === form.ratecon_email_id,
              }"
              @click="handleSelectEmail(email)"
            >
              <p class="text-sun-800 underline">{{ email.value }}</p>
            </div>
          </div>
        </div>

        <div
          v-if="canSRCD"
          class="booknow-checkbox -mt-2 flex items-center"
        >
          <CheckboxButton
            item="Request Secure Rate Confirmation Delivery in Highway."
            :active="request_secure_ratecon"
            :handle-select="handleRequest"
          />
          <a
            href="https://service.highway.com/en/articles/11060695-secure-rate-confirmation-delivery"
            target="_blank"
            class="-ml-2 pt-[1px] text-sm text-steel-900 underline"
            >Why?</a
          >
        </div>

        <p class="text-left text-lg">Dispatching Info</p>

        <div class="relative">
          <div
            class="relative flex h-10 w-full cursor-pointer items-center rounded-lg border border-steel-300"
            :class="{
              'h-20 bg-steel-100 py-3': form.dispatch_name,
              'h-10 bg-white': !form.dispatch_name,
            }"
            @click="toggleContactsDropdown"
          >
            <div class="flex flex-col items-start">
              <p class="pl-2 text-sm font-bold text-steel-900">
                {{ form.dispatch_name }}
              </p>
              <p class="pl-2 text-sm font-light text-steel-900">
                {{ form.dispatch_email }}
              </p>
              <p class="pl-2 text-sm font-light text-steel-900">
                {{ form.dispatch_phone }}
              </p>
            </div>
            <img
              class="absolute right-3 opacity-50"
              src="@/assets/arrow-down-black.svg"
              alt="carrot"
            />
          </div>
          <div
            v-if="showContactsDropdown"
            class="absolute z-20 mt-1 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
          >
            <div>
              <InputComponent
                input-label="dispatch_search"
                wrapper-class="flex  text-sm items-center gap-1.5 p-2 mx-4 my-2"
                placeholder="Search Verified Users"
                :default-input-value="form.dispatch_search"
                :value="form.dispatch_search"
                @input-change="handleSearchContacts"
              >
                <template #leftIcon>
                  <img
                    class="pt-1"
                    src="@/assets/search-light-gray.svg"
                    alt="filters"
                  /> </template
              ></InputComponent>

              <div
                v-for="contact in contacts"
                :key="contact.id"
                class="flex cursor-pointer flex-col items-start border-l-4 border-transparent px-3 py-1 hover:border-sun-400 hover:bg-sun-50"
                @click="hanelSelectContact(contact)"
              >
                <p class="font-semibold capitalize text-steel-800">
                  {{ contact.name }}
                </p>
                <p class="text-sun-800 underline">
                  {{ contact.email_address?.value }}
                </p>
                <p class="text-xs font-normal capitalize text-steel-800">
                  {{ contact.phone?.value }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="booknow-checkbox -my-2">
          <CheckboxButton
            item="Driver is same as dispatch"
            :active="driver_same_as_dispatch"
            :handle-select="handleDriverSameAsDispatch"
          />
        </div>

        <div
          v-if="!driver_same_as_dispatch"
          class="flex flex-col gap-3"
        >
          <div>
            <p class="pl-2 text-left text-sm font-semibold text-steel-800">
              Driver Name
            </p>
            <InputComponent
              input-label="driver_name"
              wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
              placeholder="Enter Name"
              :default-input-value="form.driver_name"
              :value="form.driver_name"
              @input-change="handleInput"
            />
          </div>

          <div>
            <p class="pl-2 text-left text-sm font-semibold text-steel-800">
              Driver Phone
            </p>
            <InputComponent
              input-label="driver_phone"
              wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
              placeholder="Enter Phone"
              :default-input-value="form.driver_phone"
              :value="form.driver_phone"
              @input-change="handleInput"
            />
          </div>
        </div>

        <div class="relative">
          <p class="mb-4 mt-0 text-left text-lg">
            Vehicle <span class="text-red-500">*</span>
          </p>

          <div
            class="relative flex h-10 w-full cursor-pointer items-center rounded-lg border border-steel-300"
            :class="{
              'h-16 bg-steel-100': form.vehicle?.id,
              'h-10 bg-white': !form.vehicle?.id,
            }"
            @click="toggleVehicleDropdown"
          >
            <p
              v-if="!form.vehicle?.id"
              class="pl-2 text-sm text-steel-700"
            >
              Choose Vehicle
            </p>
            <div
              v-else
              class="flex w-full cursor-pointer items-center justify-between px-4 py-2 pr-10"
            >
              <div class="flex items-start gap-2">
                <img
                  class="mt-1 h-5 w-5"
                  src="@/assets/loads/green-wifi.svg"
                  alt="wifi"
                />

                <div class="text-left">
                  <div class="flex justify-between">
                    <div class="text-left">
                      <p class="text-sm text-steel-1100">
                        {{ form?.vehicle?.truck_number || form?.vehicle.id }}
                      </p>
                    </div>
                    <div
                      v-if="form?.vehicle?.license_plate_number"
                      class="ml-2 text-right"
                    >
                      <p class="text-xs text-steel-700">
                        Plate #: {{ form?.vehicle.license_plate_number
                        }}<span v-if="form?.vehicle?.license_plate_state">
                          ({{ form?.vehicle.license_plate_state }})</span
                        >
                      </p>
                    </div>
                  </div>
                  <p class="text-xs text-steel-700">
                    {{ form?.vehicle.vehicle?.vin }}
                  </p>
                </div>
              </div>
            </div>

            <img
              class="absolute right-3 opacity-50"
              src="@/assets/arrow-down-black.svg"
              alt="carrot"
            />
          </div>

          <div
            v-if="showVehicleDropdown"
            class="absolute z-20 mt-1 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
          >
            <div v-if="filtered_carrier_vehicles?.length > 0">
              <div
                v-for="vehicle in filtered_carrier_vehicles"
                :key="vehicle.id"
                class="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-steel-100"
                @click="handleSelectVehicle(vehicle)"
              >
                <div class="flex items-start gap-2">
                  <img
                    class="mt-1 h-5 w-5"
                    src="@/assets/loads/green-wifi.svg"
                    alt="wifi"
                  />

                  <div class="text-left">
                    <div class="flex justify-between">
                      <div class="text-left">
                        <p class="text-sm text-steel-1100">
                          {{ vehicle?.truck_number || vehicle.id }}
                        </p>
                      </div>
                      <div
                        v-if="vehicle?.license_plate_number"
                        class="ml-2 text-right"
                      >
                        <p class="text-xs text-steel-700">
                          Plate #: {{ vehicle.license_plate_number
                          }}<span v-if="vehicle?.license_plate_state">
                            ({{ vehicle.license_plate_state }})</span
                          >
                        </p>
                      </div>
                    </div>
                    <p class="text-xs text-steel-700">
                      {{ vehicle.vehicle?.vin }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              <p class="text-steel-700">
                There are no current vehicles to select.
              </p>
            </div>
          </div>
        </div>

        <div>
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Trailer Number
          </p>
          <InputComponent
            input-label="trailer_number"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Trailer Number"
            :default-input-value="form.trailer_number"
            :value="form.trailer_number"
            @input-change="handleInput"
          />
        </div>
      </div>

      <div
        class="my-4 flex justify-end gap-4"
        :class="{
          'pb-[80px]': isMobile,
        }"
      >
        <ButtonComponent @click="$emit('close')">Not Now</ButtonComponent>
        <ButtonComponent
          variant="primary"
          class="flex min-w-[110px] justify-center"
          :disabled="isSubmitButtonDisabled"
          @click="handleBookNow"
        >
          <svg
            v-if="isLoading"
            class="loading relative h-5 w-full"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
              stroke="currentColor"
              stroke-width="3"
            />
            <path
              d="M16 1V7L20 4L16 1Z"
              fill="currentColor"
            />
            <path
              d="M22.4895 7.161C24.4444 8.50678 25.9564 10.402 26.8342 12.6071"
              stroke="currentColor"
              stroke-width="3"
            />
          </svg>
          <p v-else>{{ buttonText }}</p>
        </ButtonComponent>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  onBeforeMount,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import { useScreenSize } from '@/composables/useScreenSize';
import {
  BottomSheet,
  InputComponent,
  ButtonComponent,
  CheckboxButton,
} from '@/components';
import { BookLoadNotification } from '@/components/loads';
import { formatPrice, formatPriceWithoutMoneySign } from '@/utils/currency.js';

const store = useStore();

const props = defineProps({
  visible: Boolean,
  loadId: [String, Number],
});

const emit = defineEmits(['close']);

const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const form = ref({
  rate: '',
  ratecon_email: '',
  ratecon_email_id: '',
  dispatch_name: '',
  dispatch_phone: '',
  dispatch_email: '',
  trailer_number: '',
  driver_name: '',
  driver_phone: '',
  vehicle: {},
});

const currentUser = computed(() => store.state.users?.currentUser);
const load = computed(() => store.state.loads.load_details);

const rateConEmail = ref('');

const supportsBookNow = computed(() => load?.value?.supports_book_now);
const supportsPlaceOffer = computed(() => load?.value?.supports_carrier_offers);
const supportsBoth = computed(
  () => supportsBookNow.value && supportsPlaceOffer.value && load?.value?.price
);

onBeforeMount(async () => {
  const carrier_id =
    currentUser?.value?.requested_carrier_id ||
    currentUser?.value?.company?.carrier_id;

  // eslint-disable-next-line no-unused-vars
  const [_, carrierDetailRes, emailRes] = await Promise.all([
    store.dispatch('loads/getTruckPostings', {
      query: '?q[is_active_eq]=1',
    }),
    store.dispatch('carriers/getCarriersDetail', {
      id: carrier_id,
    }),
    store.dispatch('emails/getEmails', {
      query: `?q[carrier_id_eq]=${carrier_id}`,
    }),
    store.dispatch('contacts/getContactsByCarrierId', {
      carrierId: carrier_id,
    }),
    store.dispatch('loads/getCarrierVehicles', carrier_id),
  ]);

  const dispatchConactInfo =
    carrierDetailRes?.data?.contact_information?.dispatch_contact;

  const findCurrentUserEmail = emailRes?.find(
    (el) =>
      el.value === store.state.users?.currentUser?.email &&
      el?.is_type === 'rate_confirmation'
  );

  const findEmail = emailRes?.find((el) => el?.is_type === 'rate_confirmation');

  const initialEmail = findCurrentUserEmail || findEmail || emailRes[0];

  rateConEmail.value = `dot${carrierDetailRes?.data?.dot_number}@highway.com`;

  form.value.rate = formatPriceWithoutMoneySign(load?.value?.price, 0);
  form.value.ratecon_email = initialEmail?.value;
  form.value.ratecon_email_id = initialEmail?.id;
  form.value.dispatch_name = dispatchConactInfo?.name;
  form.value.dispatch_phone = dispatchConactInfo?.phone;
  form.value.dispatch_email = dispatchConactInfo?.email_address;
});

const brokerLogo = computed(() => load?.value?.broker_logo?.url);
const ratePerMile = computed(() => {
  return formatPrice(load?.value?.rate_per_mile);
});

const inputRatePerMile = computed(() => {
  if (
    parseInt(form.value.rate?.replace(',', '')) === parseInt(load.value?.price)
  ) {
    return formatPrice(load?.value?.rate_per_mile);
  } else {
    const updatedRate =
      parseInt(form.value?.rate?.replace(',', '')) /
      parseInt(load.value?.posted_miles);
    return formatPrice(updatedRate);
  }
});

const truck_postings = computed(() => store.state.loads?.truck_postings);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const filtered_carrier_vehicles = computed(() => {
  const postedTruckIds = new Set(
    truck_postings.value?.map((truck) => truck.carrier_vehicle?.id)
  );
  return carrier_vehicles.value?.filter(
    (vehicle) => !postedTruckIds.has(vehicle.id)
  );
});

const showVehicleDropdown = ref(false);
const toggleVehicleDropdown = () => {
  showVehicleDropdown.value = !showVehicleDropdown.value;
};
const handleSelectVehicle = (vehicle) => {
  form.value.vehicle = vehicle;
  toggleVehicleDropdown();
};

const showRateConDropdown = ref(false);
const toggleRateConDropdown = () => {
  showRateConDropdown.value = !showRateConDropdown.value;
};
const handleSelectEmail = (email) => {
  form.value.ratecon_email = email?.value;
  form.value.ratecon_email_id = email?.id;
  toggleRateConDropdown();
};

const ratecon_emails = computed(() => {
  const emails = store.state.emails?.emails ?? [];

  // Deduplicate by `value`
  const unique = Array.from(new Map(emails.map((e) => [e.value, e])).values());

  // Find current user's rate_confirmation email
  const currentUserEmail = store.state.users?.currentUser?.email;
  const firstIndex = unique.findIndex(
    (e) => e.is_type === 'rate_confirmation' && e.value === currentUserEmail
  );

  if (firstIndex > -1) {
    const [currentUserEmailObj] = unique.splice(firstIndex, 1);
    unique.unshift(currentUserEmailObj);
  }

  // Sort so rate_confirmation types come first
  return unique.sort((a, b) => {
    if (a.is_type === 'rate_confirmation' && b.is_type !== 'rate_confirmation')
      return -1;
    if (b.is_type === 'rate_confirmation' && a.is_type !== 'rate_confirmation')
      return 1;
    return 0;
  });
});

const showContactsDropdown = ref(false);
const toggleContactsDropdown = () => {
  showContactsDropdown.value = !showContactsDropdown.value;
};
const hanelSelectContact = (contact) => {
  form.value.dispatch_name = contact.name;
  form.value.dispatch_email = contact.email_address?.value;
  form.value.dispatch_phone = contact.phone?.value;
  toggleContactsDropdown();
};
const contacts = computed(() => {
  const dispatching_contacts = store.state.contacts?.contacts;

  return Array.from(
    new Map(dispatching_contacts.map((e) => [e.value, e])).values()
  );
});

// TODO -- add loading
const handleSearchContacts = debounce(async (text) => {
  await store.dispatch('contacts/getContactsByCarrierId', {
    carrierId: currentUser?.value?.requested_carrier_id,
    nameQuery: text,
  });
}, 300);

const driver_same_as_dispatch = ref(true);

const handleDriverSameAsDispatch = () => {
  driver_same_as_dispatch.value = !driver_same_as_dispatch.value;
};

watch(driver_same_as_dispatch, (newValue) => {
  if (newValue) {
    form.value.driver_name = form.value.dispatch_name;
    form.value.driver_phone = form.value.dispatch_phone;
  }
});

const request_secure_ratecon = ref(false);

const handleRequest = () => {
  request_secure_ratecon.value = !request_secure_ratecon.value;
};

const use_posted_offer = ref(false);

const handleUsePostedOffer = () => {
  use_posted_offer.value = !use_posted_offer.value;
};

watch(supportsBoth, (newValue) => {
  if (!newValue) {
    if (supportsBookNow?.value) {
      if (load?.value?.price) {
        use_posted_offer.value = true;
      } else {
        use_posted_offer.value = false;
      }
    } else {
      // noop
      console.log('Does not support book now');
    }
  }
});

const buttonText = computed(() =>
  use_posted_offer?.value ? 'Book Now' : 'Place Offer'
);

// check if company paywall can use Secure Rate Confirmation Delivery

// store.state.users?.currentUser?.paywall?.can_rate_confirmation
const canSRCD = computed(
  () => store.state.users.featureFlags?.fe_display_ratecon_checkbox?.enabled
);

const isLoading = computed(() => store.state.loads?.is_book_now_loading);

const debouncedRate = debounce((val) => {
  // raw digits only
  const digits = val.replace(/[^\d]/g, '');

  if (!digits) {
    form.value.rate = '';
    return;
  }

  // convert to number
  const num = parseInt(digits, 10);

  // format with commas (no decimals here, but you could add them if you want)
  form.value.rate = num.toLocaleString('en-US');
}, 200);

const handleInput = (value, name) => {
  if (name === 'rate') {
    debouncedRate(value);
  } else {
    form.value[name] = value;
  }
};

const isSubmitButtonDisabled = computed(
  () => !form.value?.ratecon_email || !form.value?.vehicle?.id
);

const handleBookNow = async () => {
  console.log('Handle book now selection', form?.value);

  const params = {
    carrier_vehicle_id: form.value.vehicle?.id,
    rate_confirmation_email_address_id: form.value?.ratecon_email_id,
    dispatch_name: form.value?.dispatch_name,
    dispatch_email: form.value?.dispatch_email,
    dispatch_phone: form.value?.dispatch_phone,
    driver_name: driver_same_as_dispatch?.value
      ? form.value?.dispatch_name
      : form.value?.driver_name,
    driver_phone: driver_same_as_dispatch?.value
      ? form.value?.dispatch_phone
      : form.value?.driver_phone,
    trailer_number: form.value?.trailer_number,
    ...(canSRCD?.value && request_secure_ratecon?.value
      ? { secure_rate_confirmation: true }
      : {}),
  };

  let res;
  if (use_posted_offer?.value) {
    // book now request
    res = await store.dispatch('loads/bookNow', {
      id: props?.loadId,
      params,
    });
  } else {
    // place offer request
    const expiresAtTime = dayjs().add(24, 'hour').format('YYYY-MM-DD HH:mm:ss');
    res = await store.dispatch('loads/placeOffer', {
      id: props?.loadId,
      params: {
        ...params,
        rate: parseInt(form.value.rate?.replace(',', '')),
        load_id: props?.loadId,
        expires_at: expiresAtTime,
      },
    });
  }

  if ([200, 201].includes(res.status)) {
    const title = use_posted_offer?.value ? 'Load Booked' : 'Offer Placed';

    const subText = use_posted_offer?.value
      ? "You've successfully booked this load."
      : "You've successfully placed an offer on this load.";
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'success',
        title,
        subText,
      },
      manuallyClose: true,
    });

    emit('close');
  } else {
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'error',
      },
      manuallyClose: true,
    });
    emit('close');
  }
};
</script>

<style>
.booknow-checkbox .container {
  @apply w-fit pl-9;
}
.booknow-checkbox .container > div {
  @apply font-normal text-steel-900;
}
.booknow-checkbox .container .checkbox-checkmark {
  @apply h-5 w-5;
}
.booknow-checkbox .container .checkbox-checkmark:after {
  border-color: black;
  width: 6px;
  height: 10px;
  border-width: 0 2px 2px 0;
  top: 4px !important;
  left: 7px;
}

.booknow-checkbox .container input:checked ~ .checkbox-checkmark {
  border-color: transparent;
}

.book-now-inputs input {
  @apply text-sm;
}

.rate-input input {
  @apply text-base !font-bold;
}
</style>
